import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  InputAdornment,
  IconButton,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import { CheckCircle, Cancel, Visibility, VisibilityOff } from "@mui/icons-material";
import { ThemeProvider } from '@mui/material/styles';
import theme from '../components/theme/theme';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from "axios";
import { jwtDecode } from "jwt-decode";

/**
 * Componente responsável pela funcionalidade de alteração de senha do usuário
 * Permite ao usuário alterar sua senha atual fornecendo:
 * - Senha atual (para validação)
 * - Nova senha (com critérios de segurança)
 * - Confirmação da nova senha
 */
const EditarSenha = () => {
  // ==================== ESTADOS DO COMPONENTE ====================

  // Estados para armazenar os valores dos campos de entrada
  const [senhaAtual, setSenhaAtual] = useState(""); // Senha atual do usuário
  const [novaSenha, setNovaSenha] = useState(""); // Nova senha a ser definida
  const [confirmarSenha, setConfirmarSenha] = useState(""); // Confirmação da nova senha

  // Estados para controlar mensagens de feedback ao usuário
  const [mensagem, setMensagem] = useState(""); // Mensagem de sucesso
  const [erro, setErro] = useState(""); // Mensagem de erro

  // Estados para controlar a visibilidade das senhas (mostrar/ocultar)
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  // ==================== FUNÇÕES DE VALIDAÇÃO ====================

  /**
   * Valida se a senha atende aos critérios de segurança estabelecidos
   * @param {string} senha - A senha a ser validada
   * @returns {Object} Objeto com cada critério e seu status (true/false)
   */
  const validarSenha = (senha) => {
    return {
      minLength: senha.length >= 8, // Mínimo 8 caracteres
      hasUpperCase: /[A-Z]/.test(senha), // Pelo menos uma letra maiúscula
      hasLowerCase: /[a-z]/.test(senha), // Pelo menos uma letra minúscula
      hasNumber: /\d/.test(senha), // Pelo menos um número
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(senha), // Caracteres especiais
    };
  };

  /**
   * Verifica se todos os critérios de segurança da senha são atendidos
   * @param {string} senha - A senha a ser verificada
   * @returns {boolean} True se todos os critérios são atendidos, false caso contrário
   */
  const senhaValida = (senha) => {
    const criterios = validarSenha(senha);
    return Object.values(criterios).every(criterio => criterio);
  };

  // Obtém os critérios de validação para a nova senha (usado na renderização)
  const criteriosSenha = validarSenha(novaSenha);

  // ==================== FUNÇÃO DE SUBMISSÃO DO FORMULÁRIO ====================

  /**
   * Manipula o envio do formulário de alteração de senha
   * Realiza validações locais e envia a requisição para o backend
   * @param {Event} e - Evento de submissão do formulário
   */
  const handleSubmit = async (e) => {
    e.preventDefault(); // Previne o comportamento padrão de recarregar a página

    // ========== VALIDAÇÕES LOCAIS ==========

    // Verifica se todos os campos foram preenchidos
    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setErro("Preencha todos os campos.");
      return;
    }

    // Verifica se a nova senha atende aos critérios de segurança
    if (!senhaValida(novaSenha)) {
      setErro("A nova senha não atende a todos os critérios de segurança.");
      return;
    }

    // Verifica se a nova senha e a confirmação são iguais
    if (novaSenha !== confirmarSenha) {
      setErro("A nova senha e a confirmação não coincidem.");
      return;
    }

    try {
      // ========== VALIDAÇÃO DO TOKEN JWT ==========

      // Recupera o token JWT do localStorage
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        setErro("Usuário não autenticado.");
        return;
      }

      // Decodifica e valida o token JWT
      let decoded;
      try {
        decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Tempo atual em segundos

        // Verifica se o token não está expirado
        if (decoded.exp && decoded.exp < currentTime) {
          setErro("Sessão expirada. Faça login novamente.");
          localStorage.removeItem("jwtToken"); // Remove token expirado
          return;
        }
      } catch (decodeError) {
        // Token mal formado ou inválido
        setErro("Token inválido. Faça login novamente.");
        localStorage.removeItem("jwtToken");
        return;
      }

      // ========== EXTRAÇÃO DO EMAIL DO TOKEN ==========

      // Extrai o email do usuário do token (tenta diferentes campos possíveis)
      const email = decoded.email || decoded.sub || decoded.user?.email;

      if (!email) {
        setErro("Email não encontrado no token.");
        return;
      }

      // ========== REQUISIÇÃO PARA O BACKEND ==========

      // Obtém a URL base da API das variáveis de ambiente
      const baseURL = process.env.REACT_APP_API_BASE_URL;

      // Envia a requisição POST para alterar a senha
      const response = await axios.post(
        `${baseURL}/update-password`,
        {
          email, // Email do usuário
          senhaAtual, // Senha atual para validação
          novaSenha, // Nova senha a ser definida
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`, // Token para autenticação
          },
        }
      );

      if (response.status === 100) {
        console.log("Tudo Ok")
      }

      // ========== SUCESSO NA ALTERAÇÃO ==========

      // Exibe mensagem de sucesso e limpa os campos
      setMensagem("Senha alterada com sucesso!");
      setErro(""); // Limpa mensagens de erro

      // Reseta todos os campos do formulário
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmarSenha("");

    } catch (error) {
      // ========== TRATAMENTO DE ERROS ==========

      console.error("Erro ao alterar senha:", error);

      // Tratamento específico baseado no status HTTP da resposta
      if (error.response?.status === 401) {
        setErro("Não autorizado. Verifique sua senha atual ou faça login novamente.");
      } else if (error.response?.status === 403) {
        setErro("Acesso negado. Você não tem permissão para esta operação.");
      } else if (error.response?.data?.message) {
        // Usa a mensagem de erro retornada pelo backend
        setErro(error.response.data.message);
      } else {
        // Erro genérico para casos não específicos
        setErro("Erro ao alterar senha. Tente novamente.");
      }
    }
  };

  // ==================== RENDERIZAÇÃO DO COMPONENTE ====================

  return (
    <ThemeProvider theme={theme}>
      {/* Container principal com layout flexbox horizontal */}
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh', // Altura mínima da viewport
          overflowX: 'auto', // Scroll horizontal se necessário
          borderRadius: 5
        }}
      >
        {/* Barra lateral com navegação principal */}
        <Sidebar />

        {/* Área principal do conteúdo (à direita da sidebar) */}
        <Box sx={{ flexGrow: 1 }}>
          {/* Cabeçalho da página com título */}
          <Header title="Configurações de Conta" />

          {/* Container principal com limitação de largura e padding */}
          <Container maxWidth="md" sx={{ p: 3 }}>
            {/* Título da seção de alteração de senha */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                Alterar Senha
              </Typography>
            </Box>

            {/* Card principal contendo o formulário */}
            <Card
              sx={{
                boxShadow: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Subtítulo do formulário */}
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: theme.palette.text.primary,
                    fontWeight: 500
                  }}
                >
                  Definir Nova Senha
                </Typography>

                {/* Formulário de alteração de senha */}
                <form onSubmit={handleSubmit} autoComplete="off">
                  <Stack spacing={3}>
                    {/* ========== CAMPO SENHA ATUAL ========== */}
                    <TextField
                      label="Senha atual"
                      type={mostrarSenhaAtual ? "text" : "password"}
                      value={senhaAtual}
                      onChange={(e) => setSenhaAtual(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      autoComplete="new-password"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {/* Botão para mostrar/ocultar senha atual */}
                            <IconButton
                              onClick={() => setMostrarSenhaAtual((prev) => !prev)}
                              edge="end"
                              sx={{ color: theme.palette.primary.main }}
                            >
                              {mostrarSenhaAtual ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* ========== CAMPO NOVA SENHA ========== */}
                    <TextField
                      label="Nova senha"
                      type={mostrarNovaSenha ? "text" : "password"}
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      autoComplete="new-password"
                      error={novaSenha && !senhaValida(novaSenha)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: novaSenha
                              ? (senhaValida(novaSenha) ? '#4caf50' : '#f44336')
                              : theme.palette.primary.main,
                          },
                          '& fieldset': {
                            borderColor: novaSenha
                              ? (senhaValida(novaSenha) ? '#4caf50' : '#f44336')
                              : 'rgba(0, 0, 0, 0.23)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: novaSenha
                              ? (senhaValida(novaSenha) ? '#4caf50' : '#f44336')
                              : theme.palette.primary.main,
                            borderWidth: '2px',
                          },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {/* Botão para mostrar/ocultar nova senha */}
                            <IconButton
                              onClick={() => setMostrarNovaSenha((prev) => !prev)}
                              edge="end"
                              sx={{ color: theme.palette.primary.main }}
                            >
                              {mostrarNovaSenha ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* ========== INDICADORES VISUAIS DOS CRITÉRIOS DA SENHA ========== */}
                    {/* Exibe apenas quando o usuário começou a digitar a nova senha */}

                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                        border: `1px solid ${theme.palette.primary.light}`,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.06) 100%)'
                      }}
                    >
                      {/* Título da seção de critérios */}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          color: theme.palette.primary.main
                        }}
                      >
                        Sua senha deve conter:
                      </Typography>

                      {/* Lista de critérios com indicadores visuais */}
                      <List dense sx={{ py: 0 }}>
                        {/* Critério: No mínimo 8 caracteres */}
                        <ListItem sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {criteriosSenha.minLength ? (
                              <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
                            ) : (
                              <Cancel sx={{ color: "error.main", fontSize: 22 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="No mínimo 8 caracteres"
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                color: criteriosSenha.minLength ? "success.main" : "error.main"
                              }
                            }}
                          />
                        </ListItem>

                        {/* Critério: Letra maiúscula */}
                        <ListItem sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {criteriosSenha.hasUpperCase ? (
                              <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
                            ) : (
                              <Cancel sx={{ color: "error.main", fontSize: 22 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Pelo menos 1 letra maiúscula"
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                color: criteriosSenha.hasUpperCase ? "success.main" : "error.main"
                              }
                            }}
                          />
                        </ListItem>

                        {/* Critério: Letra minúscula */}
                        <ListItem sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {criteriosSenha.hasLowerCase ? (
                              <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
                            ) : (
                              <Cancel sx={{ color: "error.main", fontSize: 22 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Pelo menos 1 letra minúscula"
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                color: criteriosSenha.hasLowerCase ? "success.main" : "error.main"
                              }
                            }}
                          />
                        </ListItem>

                        {/* Critério: Número */}
                        <ListItem sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {criteriosSenha.hasNumber ? (
                              <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
                            ) : (
                              <Cancel sx={{ color: "error.main", fontSize: 22 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Pelo menos 1 número"
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                color: criteriosSenha.hasNumber ? "success.main" : "error.main"
                              }
                            }}
                          />
                        </ListItem>

                        {/* Critério: Caractere especial */}
                        <ListItem sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {criteriosSenha.hasSpecialChar ? (
                              <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
                            ) : (
                              <Cancel sx={{ color: "error.main", fontSize: 22 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Pelo menos 1 caractere especial (!@#$%^&*)"
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                color: criteriosSenha.hasSpecialChar ? "success.main" : "error.main"
                              }
                            }}
                          />
                        </ListItem>
                      </List>
                    </Paper>

                    {/* ========== CAMPO CONFIRMAR SENHA ========== */}
                    <TextField
                      label="Confirmar nova senha"
                      type={mostrarConfirmarSenha ? "text" : "password"}
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      autoComplete="new-password"
                      // Aplica estilo de erro se as senhas não coincidirem
                      error={confirmarSenha && novaSenha !== confirmarSenha}
                      helperText={
                        confirmarSenha && novaSenha !== confirmarSenha
                          ? "As senhas não coincidem"
                          : ""
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: confirmarSenha
                              ? (confirmarSenha === novaSenha && senhaValida(novaSenha) ? '#4caf50' : '#f44336')
                              : theme.palette.primary.main,
                          },
                          '& fieldset': {
                            borderColor: confirmarSenha
                              ? (confirmarSenha === novaSenha && senhaValida(novaSenha) ? '#4caf50' : '#f44336')
                              : 'rgba(0, 0, 0, 0.23)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: confirmarSenha
                              ? (confirmarSenha === novaSenha && senhaValida(novaSenha) ? '#4caf50' : '#f44336')
                              : theme.palette.primary.main,
                            borderWidth: '2px',
                          },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {/* Botão para mostrar/ocultar confirmação de senha */}
                            <IconButton
                              onClick={() => setMostrarConfirmarSenha((prev) => !prev)}
                              edge="end"
                              sx={{ color: theme.palette.primary.main }}
                            >
                              {mostrarConfirmarSenha ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* ========== ALERTAS DE FEEDBACK ========== */}

                    {/* Alerta de erro - exibido quando há problemas */}
                    {erro && (
                      <Alert
                        severity="error"
                        sx={{
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            fontWeight: 500
                          }
                        }}
                      >
                        {erro}
                      </Alert>
                    )}

                    {/* Alerta de sucesso - exibido quando a operação é bem-sucedida */}
                    {mensagem && (
                      <Alert
                        severity="success"
                        sx={{
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            fontWeight: 500
                          }
                        }}
                      >
                        {mensagem}
                      </Alert>
                    )}

                    {/* ========== BOTÃO DE SUBMISSÃO ========== */}
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={
                        !senhaValida(novaSenha) ||
                        !confirmarSenha ||
                        novaSenha !== confirmarSenha
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                          boxShadow: '0 4px 8px 3px rgba(25, 118, 210, .4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          background: 'rgba(0, 0, 0, 0.12)',
                          boxShadow: 'none',
                          transform: 'none',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Alterar Senha
                    </Button>
                  </Stack>
                </form>
              </CardContent>
            </Card>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default EditarSenha;